import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  ORDER_FEATURES,
  getCraftsmanCost,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementEntityIds, selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculatePorchCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (!improvementsForTransaction || !identification) return undefined;

  const { porch, craftsmanQuality, propertyType } = improvementsForTransaction;
  const zipCode = identification.address.PostalCode;

  if (isUndefined(craftsmanQuality) || isUndefined(zipCode) || !porch || !propertyType) {
    return undefined;
  }

  const params = porch.area
    ? {
        hasFeature: ORDER_FEATURES.porch.hasFeature(improvementsForTransaction),
        propertyType,
        type: porch.type,
        quality: porch.quality ?? craftsmanQuality,
        sqft: AloftArea.fromNumber(porch.area.unitAmount, porch.area.unit),
        zipCode,
      }
    : {
        hasFeature: ORDER_FEATURES.porch.hasFeature(improvementsForTransaction),
        propertyType,
        type: porch.type,
        quality: porch.quality ?? craftsmanQuality,
        zipCode,
      };

  const calculator = getCraftsmanCost(costProps);
  return calculator.getPorchCost(params);
};

const getTotalOrBreakdownPorchCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculatePorchCost(costProps, improvements, propertyIdentification, id);

  if (!costOutput) return AloftPrice.zero;
  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalPorchCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => getTotalOrBreakdownPorchCost(costProps, improvements, propertyIdentification, id, 'totalCost');

export const selectPorchCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getTotalPorchCost,
);

export const selectZipOnlyPorchCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownPorchCost(costProps, improvements, propertyIdentification, id, 'zipOnly'),
);

export const selectPorchCostDisplay = createSelector([selectPorchCost], (porchCost) =>
  porchCost?.toString(true),
);

export const selectPorchCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectImprovementEntityIds,
  ],
  (costProps, improvements, propertyIdentification, improvementEntityIds) =>
    improvementEntityIds.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalPorchCost(costProps, improvements, propertyIdentification, propID);
      if (isDefined(cost)) acc[propID] = cost;
      return acc;
    }, {}),
);
